/* eslint-disable jsx-a11y/media-has-caption */
import React from 'react';
import _ from 'underscore';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { ClipchampPlayer } from '@bingads-webui-campaign-react/clipchamp-player';
import { withUAPLThemedStyles } from '../with-themed-styles';
import { getVideoPlacementStyles } from '../styles/ad-placement-components/video-placement-styles';
import type { UAPI18n, UAPPlacementComponentProps, UAPVideo } from '../@types';

const stubLogger = { logError: _.noop, logTrace: _.noop };


export const formatCallToActionText = (callToAction: string | undefined, i18n: UAPI18n) => (
  callToAction
    ? callToAction.replace(/([A-Z])/g, ' $1').trim()
    : i18n.getString(_TL_('Learn more'))
);

/**
 * UC video ad card component.
 *
 * The device-style placements (used by CTV and Gaming) render the selected
 * video (ClipchampPlayer) inside a distinct device frame:
 *   UC_Video_Inline          -> inline (landscape)
 *   UC_Video_MobileLandscape -> mobile landscape device frame
 *   UC_Video_MobilePortrait  -> mobile portrait device frame
 *
 * The MSN-family slots (UC_Video_MSNWatch / MSNComputer / MSNSmartphone /
 * MSNPlay) all share one desktop XANDR-style template: a 16:9 video with a
 * "Sponsored" label, a CTA button and an AdChoices corner overlay.
 *
 * Receives props through the standard library component interface:
 *   <UCVideoPlacementInner placementData={...} {...validatedPreviewTexts} i18n={i18n} loadClipchampPlayer={...} />
 */

// Maps the device-style UC video placements (CTV / Gaming) to their render style.
const VIDEO_FRAME_VARIANT = {
  UC_Video_Inline: 'inline',
  UC_Video_MobileLandscape: 'mobileLandscape',
  UC_Video_MobilePortrait: 'mobilePortrait',
} as const;

// Maps each device-style variant to its frame + inner-screen style class names.
const FRAME_VARIANT_CLASSES = {
  inline: { frame: 'deviceInline', screen: 'deviceInlineScreen' },
  mobileLandscape: { frame: 'deviceLandscape', screen: 'deviceLandscapeScreen' },
  mobilePortrait: { frame: 'devicePortrait', screen: 'devicePortraitScreen' },
} as const;

export const UCVideoPlacementInner = ({
  classes,
  videos,
  placementData,
  i18n,
  callToAction,
  loadClipchampPlayer,
}: UAPPlacementComponentProps) => {
  const {
    src,
  }: UAPVideo = videos && videos.length > 0 ? videos[0] : {};
  const { placementMeta } = placementData || {};
  const { placementType } = placementMeta || {};

  const frameVariant = placementType in VIDEO_FRAME_VARIANT
    ? VIDEO_FRAME_VARIANT[placementType as keyof typeof VIDEO_FRAME_VARIANT]
    : undefined;

  if (!src) {
    // No video selected yet: render the shared ClipchampPlayer with an empty
    // videoUrl -- exactly like the Xandr video AdPreview does. An empty url keeps
    // the player on its MP4 path, so it paints a native <video controls> with an
    // empty <source> (the black "empty player" template) instead of a "No video
    // selected" message.
    return (
      <div className={classNames(classes.placementCard, 'ad-placement-card-preview')}>
        <div className={classes.deviceColumn}>
          <div className={classes.emptyPlayer}>
            <ClipchampPlayer
              videoUrl=""
              width="100%"
              logger={stubLogger}
              loadClipchampPlayer={loadClipchampPlayer}
              autoplay={false}
              i18n={i18n}
            />
          </div>
        </div>
      </div>
    );
  }

  const player = (
    <ClipchampPlayer
      videoUrl={src}
      width="100%"
      logger={stubLogger}
      loadClipchampPlayer={loadClipchampPlayer}
      autoplay={false}
    />
  );

  // Device-style placements (CTV / Gaming): render the player inside a device frame.
  if (frameVariant) {
    const { frame, screen } = FRAME_VARIANT_CLASSES[frameVariant] || FRAME_VARIANT_CLASSES.inline;
    return (
      <div className={classNames(classes.placementCard, 'ad-placement-card-preview')}>
        <div className={classes.deviceColumn}>
          <div className={classes[frame]}>
            <div className={classes[screen]}>{player}</div>
          </div>
        </div>
      </div>
    );
  }

  // Video partners (UC_Video_VideoPartners): a single plain ClipchampPlayer --
  // no frame, overlays or template styling, just the player.
  if (placementType === 'UC_Video_VideoPartners') {
    return (
      <div className={classNames(classes.placementCard, 'ad-placement-card-preview')}>
        <div className={classes.deviceColumn}>
          <div className={classes.plainVideo}>{player}</div>
        </div>
      </div>
    );
  }

  // MSN-family slots (Watch / Computer / Smartphone / Play): shared desktop
  // XANDR-style ad -- 16:9 video with a Sponsored label, CTA button and AdChoices.
  const sponsoredText = i18n.getString(_TL_('Sponsored'));
  const ctaText = formatCallToActionText(callToAction, i18n);
  const adChoicesLabel = i18n.getString(_TL_('Ad choices'));
  return (
    <div className={classNames(classes.placementCard, 'ad-placement-card-preview')}>
      <div className={classes.msnColumn}>
        <div className={classes.msnFrame}>
          <div className={classes.msnScreen}>
            {player}
            <div className={classes.msnTopGradient} />
            <div className={classes.msnSponsored}>{sponsoredText}</div>
            <div
              className={classes.msnAdChoices}
              role="img"
              aria-label={adChoicesLabel}
            >
              <span className={classes.msnAdChoicesIcon} aria-hidden="true" />
            </div>
            <div className={classes.msnCta}>{ctaText}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

UCVideoPlacementInner.propTypes = {
  classes: PropTypes.shape({
    placementCard: PropTypes.string,
    videoPlayerRoot: PropTypes.string,
    plainVideo: PropTypes.string,
    emptyPlayer: PropTypes.string,
    deviceColumn: PropTypes.string,
    deviceInline: PropTypes.string,
    deviceInlineScreen: PropTypes.string,
    deviceLandscape: PropTypes.string,
    deviceLandscapeScreen: PropTypes.string,
    devicePortrait: PropTypes.string,
    devicePortraitScreen: PropTypes.string,
    msnColumn: PropTypes.string,
    msnFrame: PropTypes.string,
    msnScreen: PropTypes.string,
    msnTopGradient: PropTypes.string,
    msnSponsored: PropTypes.string,
    msnAdChoices: PropTypes.string,
    msnAdChoicesIcon: PropTypes.string,
    msnCta: PropTypes.string,
  }),
  videos: PropTypes.arrayOf(PropTypes.shape({
    src: PropTypes.string,
  })),
  placementData: PropTypes.shape({
    placementMeta: PropTypes.shape({
      previewTitle: PropTypes.string,
    }),
  }),
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }),
  callToAction: PropTypes.string,
  loadClipchampPlayer: PropTypes.func,
};

UCVideoPlacementInner.defaultProps = {
  classes: {},
  videos: null,
  placementData: null,
  i18n: null,
  callToAction: '',
  loadClipchampPlayer: _.noop,
};

export const UCVideoPlacement = withUAPLThemedStyles(

  (props: UAPPlacementComponentProps) => <UCVideoPlacementInner {...props} />,
  getVideoPlacementStyles,
  { componentName: 'UCVideoPlacement' }
);
